/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {AppContext} from '../../misc/appContext';
import {ActivateHandle} from '../../../util/interfaces';
import {createErrorMessage, downloadFileURL} from '../../../util/utils';
import {exportProductCosts, importProductCosts} from '../../../util/requestSender';
import {downloadFile, ResponseData} from '@dvrd/dvr-controls';
import ProductCostsUpload from './productCostsUpload';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: VoidFunction;
}

function ProductCostsUploadController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const {onSuccess} = props;
    const [importFile, setImportFile] = useState<File | null>(null);
    const [deleteCurrent, setDeleteCurrent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setImportFile(null);
        setLoading(false);
        setDeleteCurrent(false);
    }

    function onChangeDeleteCurrent(value: boolean) {
        setDeleteCurrent(value);
    }

    function onDownloadTemplate() {
        const fileURL = 'https://app.bolmate.nl/static/product-kosten-import-template-v3.xlsx';
        downloadFileURL(fileURL, 'product-kosten-import-template.xlsx');
    }

    function onDownloadProductCosts() {
        if (!account) return;
        setLoading(true);
        exportProductCosts({
            account_id: account.id,
            callback: (data: ResponseData, response: Response) => {
                if (data.status === 'success') {
                    response.blob().then((blob: Blob) => {
                        const header = response.headers.get('Content-Disposition');
                        let fileName = 'productkosten.xlsx';
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'salesDashboard.productCostsUpload.exportFailedMsg',
                                    'Het exporteren van de data is niet gelukt.'
                                )
                        ),
                        t('salesDashboard.productCostsUpload.exportFailedTitle', 'Exporteren mislukt')
                    );
                }
            }
        });
    }

    function onChangeFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (!files) return;
        setImportFile(files[0]);
    }

    function onSubmit() {
        if (!importFile || !account) return;
        setLoading(true);
        importProductCosts({
            account_id: account.id,
            delete_current: deleteCurrent,
            file: importFile,
            callback: (data: ResponseData) => {
                setLoading(false);
                onSuccess();
                if (data.success) {
                    const {errors} = data;
                    if (errors && Object.keys(errors).length) {
                        const errorContent = (
                            <label>
                                {t(
                                    'salesDashboard.productCostsUpload.partialSuccess',
                                    'Het importeren van de productkosten is deels gelukt, maar de volgende fouten zijn gevonden:'
                                )}
                                {Object.entries(errors).map(([row, error]: [string, string], idx: number) => (
                                    <span key={idx}>
                                        {t('salesDashboard.productCostsUpload.rowLabel', 'Regel')} {row}: {error}
                                        <br />
                                    </span>
                                ))}
                            </label>
                        );
                        showDialog(
                            errorContent,
                            t('salesDashboard.productCostsUpload.partialSuccessTitle', 'Importeren deels gelukt')
                        );
                    } else {
                        showSnackbar(
                            t(
                                'salesDashboard.productCostsUpload.importedSnackbar',
                                'De productkosten zijn geïmporteerd'
                            )
                        );
                        onClose();
                    }
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'salesDashboard.productCostsUpload.importFailedMsg',
                                    'Het importeren van de productkosten is niet gelukt.'
                                )
                        ),
                        t('salesDashboard.productCostsUpload.importFailedTitle', 'Importeren mislukt')
                    );
            }
        });
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <ProductCostsUpload
            onClose={onClose}
            onDownloadTemplate={onDownloadTemplate}
            onChangeFile={onChangeFile}
            onSubmit={onSubmit}
            onChangeDeleteCurrent={onChangeDeleteCurrent}
            onDownloadProductCosts={onDownloadProductCosts}
            importFile={importFile}
            loading={loading}
            active={active}
            deleteCurrent={deleteCurrent}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(ProductCostsUploadController);
